import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Briefcase } from "lucide-react";
import { toast } from "sonner";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-600 text-white">
            <Briefcase className="h-5 w-5" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
      </div>
    </div>
  );
}

export { AuthShell };

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      if (data.roles?.includes("super_admin")) navigate("/admin/organisations");
      else if (data.roles?.includes("org_admin")) navigate("/org/dashboard");
      else navigate("/employee");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Please verify your email first") {
        try { await api.post("/auth/verify-email/resend", { email }); } catch (e) {}
        navigate("/verify-email", { state: { email } });
        return;
      }
      toast.error(apiError(detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to the Infiblu Workforce Portal">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} data-testid="login-email"
            onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@company.com" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={password} data-testid="login-password"
            onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
        <div className="text-center">
          <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline" data-testid="forgot-link">
            Forgot password?
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
